import { useState, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Fetch active Stripe prices from the public /billing/prices endpoint.
 * Returns { prices, monthly, annual, loading, error }.
 *
 * Prices are cached in module scope so repeat renders skip the fetch.
 */
let cached = null;

export function usePrices() {
  const [prices, setPrices] = useState(cached?.prices ?? null);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cached) { setPrices(cached.prices); return; }

    fetch(`${API_BASE}/billing/prices`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!data.ok) throw new Error(data.code || 'Unknown error');
        cached = { prices: data.prices };
        setPrices(data.prices);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const monthly = prices?.find((p) => p.interval === 'month') ?? null;
  const annual = prices?.find((p) => p.interval === 'year') ?? null;

  return { prices, monthly, annual, loading, error };
}

/**
 * Format a Stripe amount (in cents) to a display price.
 * formatPrice(499, 'usd') → '$4.99'
 * formatPrice(4788, 'usd') → '$47.88'
 */
export function formatPrice(amount, currency = 'usd') {
  const val = (amount / 100).toFixed(2);
  const sym = currency === 'usd' ? '$' : currency.toUpperCase() + ' ';
  // Drop trailing .00 for whole-dollar amounts
  const display = val.endsWith('.00') ? val.slice(0, -3) : val;
  return `${sym}${display}`;
}
